import { useState, useEffect, Suspense } from 'react'
import { useSearchParams } from 'react-router'
import {
  Text,
  Button,
  Group,
  TextInput,
  Alert,
  Loader,
  Select,
} from '@mantine/core'
import {
  checkDeviceStatus,
  approveDeviceLink,
  useDashboards,
  useDetectPlatform,
  DeviceLinkStatus,
  ApiError,
} from './api'
import { LeaguestarState } from '../state'
import Header from '../common/components/header/Header'
import classes from './DeviceApprovalPage.module.css'

export default function DeviceApprovalPage() {
  return (
    <Suspense
      fallback={
        <div className={classes.page}>
          <Header title="Admin" />
          <div className={classes.content}>
            <div className={classes.loadingContainer}>
              <Loader size="lg" color="var(--mantine-color-primary-5)" />
              <Text c="dimmed">Loading...</Text>
            </div>
          </div>
        </div>
      }
    >
      <DeviceApprovalPageInner />
    </Suspense>
  )
}

function DeviceApprovalPageInner() {
  const { data: platformData } = useDetectPlatform()
  const [searchParams] = useSearchParams()
  const [codeInput, setCodeInput] = useState('')
  const [deviceName, setDeviceName] = useState('')
  const [dashboardId, setDashboardId] = useState<string | null>(null)
  const [status, setStatus] = useState<DeviceLinkStatus | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isApproving, setIsApproving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Set platform from domain detection (for multi-platform users)
  // If domain has a platform, use it; otherwise axios interceptor will use first platform from user's list
  useEffect(() => {
    if (platformData?.platform_id) {
      LeaguestarState.update((state) => {
        // Only override if not already set or if different from detected platform
        if (
          state.platforms.length === 0 ||
          state.platforms[0].platform_id !== platformData.platform_id
        ) {
          state.platforms = [
            { platform_id: platformData.platform_id!, platform_name: '' },
          ]
        }
      })
    }
  }, [platformData?.platform_id])
  const [success, setSuccess] = useState(false)

  const { data: dashboards } = useDashboards()

  // Get code from URL params and localStorage fallback
  const urlCode = searchParams.get('code')

  useEffect(() => {
    let codeToUse = urlCode

    // If no code in URL, check localStorage (for after login redirect)
    if (!codeToUse) {
      const storedCode = localStorage.getItem('deviceApprovalCode')
      if (storedCode) {
        codeToUse = storedCode
        localStorage.removeItem('deviceApprovalCode') // Clean up
      }
    } else {
      // Store code in localStorage in case we need to redirect to login
      localStorage.setItem('deviceApprovalCode', codeToUse)
    }

    if (codeToUse) {
      setCodeInput(codeToUse)
      handleCheckCode(codeToUse)
    }
  }, [urlCode])

  const handleCheckCode = async (code: string) => {
    if (!code) return

    setIsLoading(true)
    setError(null)
    setStatus(null)

    try {
      const deviceStatus = await checkDeviceStatus(code)
      setStatus(deviceStatus)

      if (deviceStatus.is_approved) {
        setSuccess(true)
      }
    } catch (err: unknown) {
      const apiError = err as ApiError
      setError(apiError.response?.data?.error || 'Invalid or expired code')
    } finally {
      setIsLoading(false)
    }
  }

  const handleApprove = async () => {
    const code = codeInput.trim()
    if (!code) {
      setError('No device code provided')
      return
    }
    if (!deviceName.trim()) {
      setError('Device name is required')
      return
    }

    setIsApproving(true)
    setError(null)

    try {
      await approveDeviceLink({
        code,
        device_name: deviceName.trim(),
        dashboard_id: dashboardId ? Number(dashboardId) : undefined,
      })

      // Refresh status to confirm approval
      await handleCheckCode(code)
      setSuccess(true)

      // Clean up localStorage
      localStorage.removeItem('deviceApprovalCode')
    } catch (err: unknown) {
      const apiError = err as ApiError
      setError(apiError.response?.data?.error || 'Failed to approve device')
    } finally {
      setIsApproving(false)
    }
  }

  const handleCodeInputChange = (value: string) => {
    // Format code input (remove spaces, uppercase, max 6 digits)
    const formatted = value.replace(/\D/g, '').substring(0, 6)
    setCodeInput(formatted)
    setError(null)
    setStatus(null)
    setSuccess(false)

    // Auto-check code when complete
    if (formatted.length === 6) {
      handleCheckCode(formatted)
    }
  }

  // Loading state while checking code
  if (isLoading && !status) {
    return (
      <div className={classes.page}>
        <Header title="Admin" />
        <div className={classes.content}>
          <div className={classes.loadingContainer}>
            <Loader size="lg" color="var(--mantine-color-primary-5)" />
            <Text c="dimmed">Checking device code...</Text>
          </div>
        </div>
      </div>
    )
  }

  const dashboardOptions = dashboards.map((d) => ({
    value: String(d.dashboard_id),
    label: d.name,
  }))

  return (
    <div className={classes.page}>
      <Header title="Admin" />
      <div className={classes.content}>
        <div className={classes.card}>
          <h2 className={classes.title}>Approve Device</h2>
          <p className={classes.subtitle}>
            Enter the 6-digit code shown on your device
          </p>

          <div className={classes.inputLabel}>Device Code</div>
          <TextInput
            placeholder="123456"
            value={codeInput}
            onChange={(e) => handleCodeInputChange(e.target.value)}
            size="lg"
            classNames={{
              input: classes.codeInput,
            }}
            maxLength={6}
          />

          {/* Code Valid Banner */}
          {status && !status.is_approved && !error && (
            <div className={classes.validBanner}>Device code is valid</div>
          )}

          {/* Already Approved Banner */}
          {status && status.is_approved && (
            <div className={classes.validBanner}>
              Already approved
              {status.user_name && ` by ${status.user_name}`}
            </div>
          )}

          {/* Error State */}
          {error && <div className={classes.invalidBanner}>{error}</div>}

          {/* Approval Form - Show if code is valid and not already approved */}
          {status && !status.is_approved && !success && (
            <>
              <TextInput
                label="Device Name"
                placeholder="e.g. Conference Room TV"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                description="Give this device a name to identify it later"
                mt="md"
                required
              />

              {dashboardOptions.length > 0 && (
                <Select
                  label="Dashboard"
                  placeholder="Select a dashboard (optional)"
                  data={dashboardOptions}
                  value={dashboardId}
                  onChange={setDashboardId}
                  description="Choose which dashboard this device should display"
                  mt="md"
                  clearable
                />
              )}

              <Button
                onClick={handleApprove}
                loading={isApproving}
                disabled={!deviceName.trim()}
                size="lg"
                className={classes.approveButton}
              >
                Approve Device
              </Button>
            </>
          )}

          {/* Show approve button when no code yet */}
          {!status && !success && (
            <Button
              onClick={() => handleCheckCode(codeInput)}
              disabled={codeInput.length !== 6}
              loading={isLoading}
              size="lg"
              className={classes.approveButton}
            >
              Approve Device
            </Button>
          )}

          {/* Success State */}
          {success && (
            <Alert color="green" className={classes.successAlert}>
              <Group>
                <div>
                  <Text fw={500}>Device Approved Successfully!</Text>
                  <Text size="sm">
                    The device can now access your dashboard. You can close this
                    page.
                  </Text>
                </div>
              </Group>
            </Alert>
          )}

          {/* How it works section */}
          {!codeInput && (
            <div className={classes.howItWorks}>
              <div className={classes.howItWorksTitle}>How this works:</div>
              <ol className={classes.howItWorksList}>
                <li className={classes.howItWorksItem}>
                  Your device shows 6-digit code
                </li>
                <li className={classes.howItWorksItem}>
                  Enter the code above or scan the QR code
                </li>
                <li className={classes.howItWorksItem}>
                  Click "Approve Device" to grant access
                </li>
                <li className={classes.howItWorksItem}>
                  The device will automatically detect the approval
                </li>
              </ol>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
